import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Divider,
  Chip
} from '@mui/material';
import {
  Business,
  Check,
  Error as ErrorIcon,
  Login,
  PersonAdd
} from '@mui/icons-material';
import { useProfile } from '../context/userContext';
import styles from '../styles/InvitationAccept.module.css';

const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const { profile } = useProfile();

  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/invitations/accept/${token}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch invitation details');
        }

        const data = await response.json();
        setInvitation(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchInvitationDetails();
    }
  }, [token]);

  // Check email match when authenticated
  const emailMatches = isAuthenticated && profile?.email && invitation?.inviteeEmail &&
    profile.email.toLowerCase() === invitation.inviteeEmail.toLowerCase();

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: `/invitations/accept/${token}`,
        intent: 'accept_invitation'
      }
    });
  };

  const handleSignup = () => {
    loginWithRedirect({
      appState: {
        returnTo: `/invitations/accept/${token}`,
        intent: 'accept_invitation'
      },
      screen_hint: 'signup'
    });
  };

  const handleAcceptInvitation = async () => {
    try {
      setAccepting(true);
      setError(null);

      const accessToken = await getAccessTokenSilently();

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/invitations/accept/${token}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }

      const data = await response.json();
      setSuccess(true);

      // Store organization ID and redirect to dashboard after 1 second
      const orgId = data.organization?.organizationId || data.organizationId;
      if (orgId) {
        localStorage.setItem('currentOrganizationId', orgId);
        localStorage.setItem('viewMode', 'organization');

        // Set account switching flag for smooth transition
        sessionStorage.setItem('accountSwitching', 'true');


        setTimeout(() => {
          window.location.href = '/dashboard'; // Full reload to refresh context
        }, 1000);
      } else {
        throw new Error('Invalid response: Missing organization ID');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return '#d32f2f';
      case 'admin':
        return '#f57c00';
      case 'member':
        return '#1976d2';
      default:
        return '#666666';
    }
  };

  const getRoleDescription = (role) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'Full administrative access including billing and organization management';
      case 'admin':
        return 'Team management and full access to fleet and operations';
      case 'member':
        return 'Standard access to organization features';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" className={styles.container}>
        <Paper className={styles.paper} elevation={3}>
          <Box className={styles.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading invitation...
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (error && !invitation) {
    return (
      <Container maxWidth="sm" className={styles.container}>
        <Paper className={styles.paper} elevation={3}>
          <Box className={styles.errorContainer}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invalid or Expired Invitation
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This invitation link may have expired or already been used. Please contact the organization administrator for a new invitation.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{ mt: 2 }}
            >
              Go to Home
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" className={styles.container}>
        <Paper className={styles.paper} elevation={3}>
          <Box className={styles.successContainer}>
            <Check color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invitation Accepted!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You've successfully joined <strong>{invitation.organizationName}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to organization dashboard...
            </Typography>
            <CircularProgress size={32} sx={{ mt: 2 }} />
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className={styles.container}>
      <Paper className={styles.paper} elevation={3}>
        {/* Header */}
        <Box className={styles.header}>
          <Business sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Organization Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You've been invited to join an organization
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Invitation Details */}
        <Box className={styles.invitationDetails}>
          <Typography variant="h6" gutterBottom>
            Organization
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {invitation?.organizationName}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Your Role
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={invitation?.role}
              sx={{
                backgroundColor: `${getRoleBadgeColor(invitation?.role)}15`,
                color: getRoleBadgeColor(invitation?.role),
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'capitalize'
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {getRoleDescription(invitation?.role)}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Invited Email
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#666' }}>
            {invitation?.inviteeEmail}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Authentication Section */}
        {!isAuthenticated && (
          <Box className={styles.authSection}>
            <Alert severity="info" sx={{ mb: 2 }}>
              You need to sign in or create an account with <strong>{invitation?.inviteeEmail}</strong> to accept this invitation.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Login />}
                onClick={handleLogin}
              >
                Sign In
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<PersonAdd />}
                onClick={handleSignup}
              >
                Create Account
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              You must use the email address <strong>{invitation?.inviteeEmail}</strong>
            </Typography>
          </Box>
        )}

        {isAuthenticated && !emailMatches && (
          <Box className={styles.authSection}>
            <Alert severity="error">
              <Typography variant="body2" gutterBottom>
                <strong>Email Mismatch</strong>
              </Typography>
              <Typography variant="body2">
                You're signed in as <strong>{profile?.email}</strong>, but this invitation was sent to <strong>{invitation?.inviteeEmail}</strong>.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please sign out and sign in with the invited email address to accept this invitation.
              </Typography>
            </Alert>
          </Box>
        )}

        {isAuthenticated && emailMatches && (
          <Box className={styles.authSection}>
            <Alert severity="success" sx={{ mb: 2 }}>
              You're signed in with the correct email address. You can now accept this invitation.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={handleAcceptInvitation}
              disabled={accepting}
              startIcon={accepting ? <CircularProgress size={20} /> : <Check />}
            >
              {accepting ? 'Accepting...' : 'Accept Invitation'}
            </Button>
          </Box>
        )}

        {error && invitation && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default InvitationAccept;
